import type { FC } from 'react';
import React from 'react';
import { Button, Dialog, Stack, Typography } from '@packages/shared';
import { ArrowMiniRight32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { graphql, getFragmentData } from '@packages/gql/generated/shopping';
import { ProductSelectionSelectMessages } from './messages';
import { ColorTile } from './ColorTile';
import { DialogVariationSelection } from './DialogVariationSelection';
import { disabledStyle, defaultStyle } from '../styles';

const SelectValuesFragment = graphql(/* GraphQL */ `
  fragment SelectValuesFragment on ProductDimension {
    displayName
    values {
      displayText
      image
      variationMainImage
      isSelected
      rgb
      sku
      availability {
        state
        message
      }
    }
  }
`);

export type ProductSelectionSelectProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof SelectValuesFragment>;
  onChange?: (sku: string) => void;
};

/**
 * ProductSelectionSelect component
 *
 * used to display the selected dimension
 * opens dialog on click to select a different variation
 *
 * */
export const ProductSelectionSelect: FC<ProductSelectionSelectProps> = ({
  dimensionData,
  onChange,
}) => {
  const { values, displayName } = getFragmentData(SelectValuesFragment, dimensionData);
  const [open, setOpen] = React.useState(false);
  const { formatMessage } = useIntl();

  const selectedVariation = values.find((variation) => variation.isSelected === true);
  const isDisabled = values.length === 1;
  const style = isDisabled ? disabledStyle : defaultStyle;

  const openDialogHandler = () => {
    setOpen(true);
  };
  const closeDialogHandler = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        onClick={openDialogHandler}
        sx={{ ...style, px: 1.5 }}
        fullWidth
        disabled={isDisabled}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          width="100%"
        >
          {selectedVariation && (selectedVariation.rgb || selectedVariation.image) && (
            <ColorTile
              imageSize="BUTTON"
              rgb={selectedVariation.rgb}
              image={selectedVariation.image}
              displayText={selectedVariation.displayText || ''}
            />
          )}
          <Typography variant="body2" sx={{ flex: '1 1 100%' }} component="span" textAlign="left">
            {selectedVariation !== undefined
              ? selectedVariation.displayText
              : formatMessage(ProductSelectionSelectMessages.SelectVariation)}
          </Typography>
          {!isDisabled && <ArrowMiniRight32 sx={{ flex: '0 0 32px', height: '32px' }} />}
        </Stack>
      </Button>
      <Dialog
        title={formatMessage(ProductSelectionSelectMessages.DialogHeadline, {
          dimension: displayName,
        })}
        dialogContent={
          <DialogVariationSelection
            valuesData={values}
            handleClose={closeDialogHandler}
            onChange={onChange}
          />
        }
        dialogContentSxProps={{ bgcolor: 'common.white' }}
        open={open}
        handleClose={closeDialogHandler}
      />
    </>
  );
};
